import { Injectable } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { TwcLanguageService } from '@topcon/twc-angular';

import { BaseTranslationService } from '@mirage-projects/cw-models';

import { MagnetService } from '../../abstract-classes/magnet.service';
import { LocalStorageService } from '../local-storage/local-storage.service';

export interface LanguageConfiguration {
    name: string;
    pcvizCode: string;
    twcCode: string;
    localized: string;
    default?: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class TranslationService extends BaseTranslationService {
    readonly LANGUAGES!: Readonly<LanguageConfiguration>[];
    readonly DEFAULT_LANGUAGES: Readonly<LanguageConfiguration>[] = [
        {
            name: 'English',
            pcvizCode: 'en-us',
            twcCode: 'en_US',
            localized: 'English',
            default: true,
        },
        {
            name: 'Spanish',
            pcvizCode: 'es-es',
            twcCode: 'es',
            localized: 'Español',
        },
        {
            name: 'Japanese',
            pcvizCode: 'ja',
            twcCode: 'ja',
            localized: '日本語',
        },
        {
            name: 'Chinese',
            pcvizCode: 'zh-cn',
            twcCode: 'zh_CN',
            localized: '中文',
        },
        {
            name: 'Italian',
            pcvizCode: 'it',
            twcCode: 'it',
            localized: 'Italiano',
        },
        {
            name: 'German',
            pcvizCode: 'de',
            twcCode: 'de',
            localized: 'Deutsch',
        },
        {
            name: 'French',
            pcvizCode: 'fr-fr',
            twcCode: 'fr',
            localized: 'Français',
        },
        {
            name: 'Portuguese',
            pcvizCode: 'pt-pt',
            twcCode: 'pt',
            localized: 'Português',
        },
        {
            name: 'Dutch',
            pcvizCode: 'nl-nl',
            twcCode: 'nl',
            localized: 'Nederlands',
        },
    ];

    defaultLanguageIndex: number;
    defaultTwcLang: string;
    defaultPcvizLang: string;
    onChangeLanguage$: BehaviorSubject<string>;

    private readonly LOCALSTORAGE_LANGUAGE = 'language';

    constructor(
        private translateService: TranslateService,
        private twcLanguageService: TwcLanguageService,
        private localStorageService: LocalStorageService,
        private magnetService: MagnetService
    ) {
        super();

        // TODO: Remove when Collage Web implements chinese language
        this.LANGUAGES = this.magnetService.isSite()
            ? this.DEFAULT_LANGUAGES
            : this.DEFAULT_LANGUAGES.filter((lang) => lang.twcCode !== 'zh_CN');

        this.defaultLanguageIndex = this.LANGUAGES.findIndex((item) => item.default);
        this.defaultTwcLang = this.LANGUAGES[this.defaultLanguageIndex].twcCode;
        this.defaultPcvizLang = this.LANGUAGES[this.defaultLanguageIndex].pcvizCode;
        this.onChangeLanguage$ = new BehaviorSubject<string>(this.defaultTwcLang);

        this.init();
    }

    init(): void {
        const navigatorLang = navigator.language.substring(0, 2);
        if (this.existLocaleByCode(navigatorLang)) {
            this.defaultTwcLang = this.getTwcLocale(navigatorLang);
            this.defaultPcvizLang = this.getPcvizLocale(navigatorLang);
        }

        // Default lang
        this.translateService.setDefaultLang(this.defaultTwcLang);
        const currentLanguage: string = this.localStorageService.getItem(this.LOCALSTORAGE_LANGUAGE) ?? this.defaultPcvizLang;
        this.changeLanguage(currentLanguage);
    }

    controlLanguageUser(languageUser: string): void {
        const languageStorage = this.localStorageService.getItem(this.LOCALSTORAGE_LANGUAGE);
        if (languageStorage) {
            this.changeLanguage(languageStorage);
        } else {
            const localeUser = this.getLocaleByName(languageUser);
            if (localeUser) {
                this.changeLanguage(localeUser);
            }
        }
    }

    getLocaleByName(langName: string): string | null {
        const findLang = this.LANGUAGES.find((language) => language.name === langName);
        if (!findLang) {
            return null;
        }
        return findLang.pcvizCode;
    }

    /**
     * Checks if the passed language code exists in our app
     * @param shortCode two digit language code
     */
    existLocaleByCode(shortCode: string): boolean {
        return this.LANGUAGES.some((language) => language.pcvizCode.startsWith(shortCode));
    }

    /**
     * Gets the equivalent twc language code from the passed one
     * @param shortCode two digit language code to compare with the language list (only the language, not the region in the code)
     */
    getTwcLocale(shortCode: string): string {
        return this.LANGUAGES.find((language) => language.pcvizCode.startsWith(shortCode))?.twcCode ?? this.defaultTwcLang;
    }

    /**
     * Gets the equivalent pcviz language code from the passed one
     * @param shortCode two digit language code to compare with the language list
     */
    getPcvizLocale(shortCode: string): string {
        return this.LANGUAGES.find((language) => language.pcvizCode.startsWith(shortCode))?.pcvizCode ?? this.defaultPcvizLang;
    }

    get currentLang(): string {
        return this.translateService.currentLang;
    }

    /**
     * Changes the current application language and stores it in the local storage
     * @param pcvizCode Language to change
     */
    changeLanguage(pcvizCode: string): void {
        const shortCode = pcvizCode.substring(0, 2);
        const twcLocale = this.getTwcLocale(shortCode);
        this.twcLanguageService.setLanguage(twcLocale);
        this.translateService.use(twcLocale).subscribe(() => {
            this.onChangeLanguage$.next(twcLocale);
        });

        this.localStorageService.setItem(`${this.LOCALSTORAGE_LANGUAGE}`, pcvizCode, true);
    }

    translate(key: string | undefined, params?: any): string {
        if (!key) {
            return '';
        }
        return this.translateService.instant(key, params);
    }

    translateAsync(key: string, params?: any): Observable<string> {
        return this.translateService.get(key, params);
    }

    existKey(key: string): boolean {
        return this.translate(key) !== key;
    }

    translateWithDefault(key: string | undefined, defaultKey: string | undefined, params?: any): string {
        if (!key) {
            return this.translate(defaultKey, params);
        }
        return this.existKey(key) ? this.translate(key, params) : (this.translate(defaultKey, params) ?? '');
    }
}
